import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SUGGESTED = [
  "What is a nervous system reset?",
  "How does acupuncture help anxiety?",
  "Can I rent a room as a practitioner?",
  "How do I book a session?",
];

function getSessionId() {
  let id = localStorage.getItem("sano_chat_session");
  if (!id) {
    id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("sano_chat_session", id);
  }
  return id;
}

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm Sano — your guide to acupuncture, nervous-system care and our studio. Ask me anything, gently.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId.current,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I'm having trouble responding right now. Please email hello@sanoacu.com or book directly at sanoacu.com.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-6 z-50 h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}
        aria-label="Open chat with Sano"
        data-testid="chat-toggle"
      >
        {open ? <X size={22} strokeWidth={1.5} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          className="fixed bottom-40 right-6 z-50 w-[92vw] sm:w-[420px] h-[600px] max-h-[80vh] border rounded-sm shadow-2xl flex flex-col overflow-hidden"
          style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
          data-testid="chat-window"
        >
          <div
            className="p-5 border-b flex items-center gap-3"
            style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg-2)" }}
          >
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center"
              style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}
            >
              <Sparkles size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif-display text-xl leading-tight">Ask Sano</div>
              <div className="text-xs" style={{ color: "var(--sano-ink-2)" }}>
                Acupuncture · Nervous system · Booking
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 scroll-soft" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[85%] px-4 py-3 text-sm leading-relaxed rounded-sm whitespace-pre-wrap"
                  style={{
                    background: m.role === "user" ? "var(--sano-olive)" : "var(--sano-bg-3)",
                    color: m.role === "user" ? "var(--sano-bg)" : "var(--sano-ink)",
                  }}
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-3 text-sm rounded-sm flex gap-1.5"
                  style={{ background: "var(--sano-bg-3)" }}
                  data-testid="chat-loading"
                >
                  <Dot /><Dot delay={150} /><Dot delay={300} />
                </div>
              </div>
            )}
          </div>

          {messages.length < 2 && (
            <div className="px-5 pb-2 flex flex-wrap gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 border rounded-full hover:bg-[#EAEAE0]"
                  style={{ borderColor: "var(--sano-border)" }}
                  data-testid={`chat-suggest-${s.slice(0, 10).replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t p-3 flex gap-2"
            style={{ borderColor: "var(--sano-border)" }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about acupuncture, booking, the studio…"
              className="flex-1 px-4 py-2.5 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-[#3A5A40]"
              style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="h-10 w-10 rounded-full flex items-center justify-center disabled:opacity-40"
              style={{ background: "var(--sano-olive)", color: "var(--sano-bg)" }}
              data-testid="chat-send"
              aria-label="Send"
            >
              <Send size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Dot({ delay = 0 }) {
  return (
    <span
      className="h-1.5 w-1.5 rounded-full inline-block"
      style={{
        background: "var(--sano-olive)",
        animation: "softUp 0.9s ease-in-out infinite alternate",
        animationDelay: `${delay}ms`,
      }}
    />
  );
}
